import {
  addDataSink,
  addDataSourceFromForm,
  setError,
  updateDataSourceFromForm,
  updateSinkInfo,
} from 'js/actions';
import {
  ADD_DATA_SOURCE_REQUEST,
  ADD_SINK_REQUEST,
  GET_DATA_SOURCE_REQUEST,
  GET_SINK_REQUEST,
  UPDATE_SINK_INFO_REQUEST,
  UPDATE_SOURCE_INFO_REQUEST,
} from 'js/constants/actionTypes';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import { FORM_TYPE_CREATE_CDC } from 'js/constants/formTypes';
import { FLOW_TYPE_CDC } from 'js/constants/pipelineConstants';
import { SearchFilterTypes } from 'js/constants/visibilityFilters';
import UIUtils from 'js/lib/UIUtils';
import { getTableNameValidationMessage, isValidDBColumnName } from 'js/lib/ValidationUtils';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import createFlowService from 'new/components/flows/service/createFlowService';
import { isUiModeCode } from 'new/utils/uiMode';
import useCDC from 'new/components/flows/types/cdc/CreateCDCFlow/CDCFlow/useCDC';
import useFlowParams from 'new/components/flows/useFlowParams';
import { useForm } from 'new/elements/Form/useForm';
import useProbeTree from 'new/hooks/adapters/useProbeTree';
import { usePreviousPath } from 'new/hooks/usePreviousPath';
import usePaging from 'new/state/paging/repository/usePaging';
import useProbe from 'new/state/probe/repository/useProbe';
import {
  assignFlowToProjectIfPresent,
  redirectToProjectIfNeeded,
} from 'new/utils/projectAssignment';
import { useHeaderMetaConfig } from 'new/components/flows/context/useHeaderMetaConfig';
import { useCallback, useEffect } from 'react';
import type { MutableRefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

export default function useResourceCreation(
  editingId,
  pendingResourceConfigRef?: MutableRefObject<any>,
) {
  const currentProjectId = useSelector(getCurrentProjectId);
  const { name: metaName, description: metaDescription } = useHeaderMetaConfig();

  const {
    connectionType,
    selectedConnection,
    selectedCredential,
    resourceConfig,
    updateFormData,
    nexset,
    sinkConfig,
    sink,
    source,
    credentialId,
  } = useCDC();

  const { isModal, onDismiss } = useFlowParams();

  const { setLoaderMessages } = useForm();
  const flowService = createFlowService();

  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const history = useHistory();

  const sourceConfigById = useSelector((state: any) => state.paging.sourceConfigById);
  const metaInfoById = useSelector((state: any) => state.paging.metaInfoById);
  const { getSinkConfigFields, getSourceConfigFields } = usePaging();

  //TODO: NEX-15904: this is a temporary fix to allow the creation of a sink without a path for file connectors. We need to refactor this to be more generic & to handle other scenarios.
  const configFields =
    selectedConnection !== 's3_iceberg'
      ? sourceConfigById[selectedConnection]
      : getSinkConfigFields(selectedConnection, FLOW_TYPE_CDC);

  const { connectionCategory } = metaInfoById[selectedConnection] || {};

  const { databases } = useProbeTree(credentialId);

  const { getProbeData } = useProbe();
  const previousPath = usePreviousPath();
  const probe = getProbeData(credentialId);

  useEffect(() => {
    setLoaderMessages({
      [UPDATE_SOURCE_INFO_REQUEST]: 'Updating source info',
      [GET_SINK_REQUEST]: 'Loading destination',
      [GET_DATA_SOURCE_REQUEST]: 'Loading source',
      [ADD_DATA_SOURCE_REQUEST]: 'Creating source',
      [UPDATE_SINK_INFO_REQUEST]: 'Updating destination info',
      [ADD_SINK_REQUEST]: 'Creating destination',
    });
  }, [probe.loading]);

  const getEffectiveResourceConfig = () => pendingResourceConfigRef?.current || resourceConfig;

  const createNewSource = () => {
    const effectiveResourceConfig = getEffectiveResourceConfig();
    const userSourceConfig = effectiveResourceConfig.resourceConfig.source_config;
    let sourceConfig;

    // NEX-21470: in code mode the user's pasted JSON is the source of truth.
    // Skip default-injection (database, table, cdc.*, mapping) and the
    // configWithMissingDefaults / reduceConfig passes that would re-shape it.
    if (isUiModeCode(userSourceConfig)) {
      sourceConfig = { ...userSourceConfig };
    } else {
      sourceConfig = {
        ...createDefaultSourceConfig(),
        ...source?.source_config,
        ...userSourceConfig,
        database: databases?.[0]?.name,
        table: databases?.[0]?.children?.[0]?.name,
      };
      removeEmptyProperties(sourceConfig);
      sourceConfig = UIUtils.configWithMissingDefaults(sourceConfig, configFields);
      sourceConfig = UIUtils.reduceConfig(sourceConfig, configFields);
    }

    return {
      ...effectiveResourceConfig.resourceConfig,
      name:
        metaName ||
        effectiveResourceConfig.resourceConfig?.name ||
        source?.name ||
        flowService.getSourceName({
          selectedCredential,
          connectionCategory,
        }),
      description: metaDescription || effectiveResourceConfig.resourceConfig?.description || '',
      source_config: sourceConfig,
    };
  };

  const requestEditSource = (newSource, runWithIngestionValue = false) => {
    return dispatch(
      updateDataSourceFromForm(
        FORM_TYPE_CREATE_CDC,
        editingId,
        newSource,
        connectionCategory,
        false,
        credentialId,
        null,
        null,
        runWithIngestionValue,
      ),
    );
  };

  const requestCreateSource = (newSource) => {
    return dispatch(
      addDataSourceFromForm(
        FORM_TYPE_CREATE_CDC,
        selectedConnection,
        connectionCategory,
        newSource,
        credentialId,
      ),
    );
  };

  const requestSource = async (newSource, runWithIngestionValue = false) => {
    let response: any;
    if (!!editingId) {
      response = await requestEditSource(newSource, runWithIngestionValue);
    } else {
      response = await requestCreateSource(newSource);
    }
    return response?.payload?.formData?.source;
  };

  const navigateAfterRequest = async (source: any) => {
    if (source?.id) {
      if (isModal) {
        onDismiss?.({ addedDataSourceId: source.id, ...source });
        return;
      }
      if (editingId) {
        // Editing existing source - just go back
        history.goBack();
      } else {
        // Creating new source - navigate to edit screen if it created a new flow
        try {
          const flowNodeId = source?.origin_node_id || source?.flow_node_id;

          const assignedProjectId = await assignFlowToProjectIfPresent({
            dispatch,
            flowNodeId,
            currentProjectId,
          });

          if (assignedProjectId) {
            redirectToProjectIfNeeded(history, assignedProjectId, dispatch);
            return;
          }
        } catch (error) {
          console.error('❌ CDC: Failed to assign flow to project:', error);
        }
        const flowOriginId = source?.origin_node_id || null;
        history.replace(
          flowService.getPathToGoAfterCreation(previousPath, undefined, {
            resourceType: 'source',
            flowType: 'cdc',
            flowOriginId,
          }),
        );
      }
    }
  };

  const createDefaultSourceConfig = () => {
    return {
      source_type: selectedConnection,
      'cdc.enabled': true,
      'cdc.mode': 'multi_table',
      mapping: {
        mode: 'auto',
      },
    };
  };

  const createSource = useCallback(
    async (runWithIngestionValue = false) => {
      const newSource = createNewSource();
      try {
        const source = await requestSource(newSource, runWithIngestionValue);
        if (source?.id) {
          updateFormData({
            credentialId: null,
            selectedCredentialInfo: null,
            connectionType: 'SINK',
            selectedConnection: null,
            resourceConfig: null,
          });
          navigateAfterRequest(source);
        }
        return !!source?.id;
      } catch (e) {
        return false;
      }
    },
    [databases, resourceConfig, configFields, connectionCategory, selectedConnection, credentialId],
  );

  const createDefaultSinkConfig = () => {
    if (connectionCategory !== CONNECTION_CATEGORY_DATABASE) return {};
    return {
      'cdc.enabled': true,
      'cdc.mode': 'multi_table',
      database: databases?.[0]?.name,
      mapping: {
        mode: 'auto',
        tracker_mode: 'NONE',
      },
      sink_type: selectedConnection,
      'insert.mode': 'UPSERT',
      'upsert.nulls': true,
    };
  };

  const createNewSink = () => {
    const effectiveResourceConfig = getEffectiveResourceConfig();
    const config = structuredClone(sinkConfig || {});
    const configName = metaName || config?.name || config?.sourceName;
    const configDescription = metaDescription || config?.description || config?.sourceDescription;

    delete config.name;
    delete config.sourceName;
    delete config.description;
    delete config.sourceDescription;

    // NEX-21470: in code mode the user owns sink_config — skip CDC default
    // injection (cdc.enabled, cdc.mode, database, mapping, insert.mode,
    // upsert.nulls, sink_type) and configWithMissingDefaults / reduceConfig.
    const userSinkConfig = effectiveResourceConfig.resourceConfig?.sink_config;
    const codeMode = isUiModeCode(userSinkConfig) || isUiModeCode(config);
    let newSinkConfig;

    if (codeMode) {
      newSinkConfig = { ...(userSinkConfig ?? config) };
    } else {
      newSinkConfig =
        connectionCategory === CONNECTION_CATEGORY_DATABASE
          ? {
              ...createDefaultSinkConfig(),
              ...sink?.sink_config,
              database: databases?.[0]?.name,
              ...userSinkConfig,
            }
          : config;

      removeEmptyProperties(newSinkConfig);
      newSinkConfig = UIUtils.configWithMissingDefaults(newSinkConfig, configFields);
      newSinkConfig = UIUtils.reduceConfig(newSinkConfig, configFields);
    }

    return {
      ...effectiveResourceConfig.resourceConfig,
      // Ensures edit payload includes the (possibly changed) credential — on create
      // it's also set by addDataSink, but updateSinkInfo doesn't inject it.
      data_credentials_id: credentialId,
      name:
        configName ||
        effectiveResourceConfig?.resourceConfig?.name ||
        sink?.name ||
        flowService.getSinkName({
          connectionCategory,
          selectedConnection,
          selectedCredential,
        }),
      description: configDescription || '',
      data_set_id: nexset?.id,
      sink_config: newSinkConfig,
    };
  };

  const canCreateSink = (newSink) => {
    const { sink_config } = newSink;
    const tablePrefix = sink_config?.mapping?.table_name_prefix;

    if (
      tablePrefix &&
      !isValidDBColumnName(tablePrefix, selectedConnection, {
        skipBeginWithCheck: false,
      })
    ) {
      const message = getTableNameValidationMessage(selectedConnection, 'table prefix');
      dispatch(setError({ message }));
      return false;
    }

    const tableSuffix = sink_config?.mapping?.table_name_suffix;

    if (
      tableSuffix &&
      !isValidDBColumnName(tableSuffix, selectedConnection, {
        skipBeginWithCheck: true,
      })
    ) {
      const message = getTableNameValidationMessage(selectedConnection, 'table suffix');
      dispatch(setError({ message }));
      return false;
    }

    return true;
  };

  const requestSinkCreation = async (newSink) => {
    let response;
    if (editingId) {
      response = await dispatch(updateSinkInfo(editingId, newSink));
    } else {
      response = await dispatch(addDataSink(selectedConnection, newSink, credentialId));
    }
    return response?.entities?.sinks?.[response?.result];
  };

  const redirectAfterCreation = async (sink) => {
    if (sink?.id) {
      if (isModal) {
        onDismiss?.({ addedDataSinkId: sink.id, ...sink });
        return;
      }
      if (editingId) {
        history.goBack();
      } else {
        try {
          const flowNodeId = sink?.origin_node_id || sink?.flow_node_id;

          const assignedProjectId = await assignFlowToProjectIfPresent({
            dispatch,
            flowNodeId,
            currentProjectId,
          });

          if (assignedProjectId) {
            redirectToProjectIfNeeded(history, assignedProjectId, dispatch);
            return;
          }
        } catch (error) {
          console.error('❌ CDC: Failed to assign sink to project:', error);
        }

        history.replace(
          flowService.getPathToGoAfterCreation(previousPath, {
            search: `${SearchFilterTypes.DESTINATION_ID}:${sink?.id}`,
          }),
        );
      }
    }
  };

  const createSink = useCallback(async () => {
    const newSink = createNewSink();
    if (!canCreateSink(newSink)) return false;
    try {
      const sink = await requestSinkCreation(newSink);
      redirectAfterCreation(sink);
      return !!sink?.id;
    } catch (e) {
      return false;
    }
  }, [
    source,
    nexset,
    sinkConfig,
    resourceConfig,
    configFields,
    connectionCategory,
    selectedConnection,
    credentialId,
  ]);

  const removeEmptyProperties = (config: any) => {
    // remove config properties which have been manually set to empty values so
    // that they can be filled with defaults
    // example use case: "Database Name" field in Iceberg
    for (const key in config) {
      if (config[key] === '') {
        delete config[key];
      }
    }
  };

  return useCallback(
    async (runWithIngestionValue = false) => {
      try {
        if (connectionType === 'SOURCE') {
          const result = await createSource(runWithIngestionValue);
          return result;
        }
        const result = await createSink();
        return result;
      } finally {
        if (pendingResourceConfigRef) {
          pendingResourceConfigRef.current = null;
        }
      }
    },
    [createSource, createSink, connectionType, pendingResourceConfigRef],
  );
}
